import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Send, Loader2, LifeBuoy, Bot, User } from "lucide-react";

function getSessionId() {
  let sid = localStorage.getItem("oa_support_session");
  if (!sid) {
    sid = "sup_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("oa_support_session", sid);
  }
  return sid;
}

const GREETING = {
  role: "assistant",
  content:
    "Hi! I'm the A.R.I.A. support assistant. Ask me about pricing, billing, how scans work, monitoring, or your account — I'll help right away and loop in a human if needed.",
};

export function SupportChat({ compact = false }) {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getSessionId);
  const scrollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    api
      .get(`/support/history?session_id=${sessionId}`)
      .then(({ data }) => {
        if (data.messages?.length) {
          setMessages([GREETING, ...data.messages.map((m) => ({ role: m.role, content: m.content, escalated: m.escalated }))]);
        }
      })
      .catch(() => {})
      .finally(scrollToBottom);
  }, [sessionId, scrollToBottom]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: data.answer, escalated: data.escalated }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I couldn't reach support right now. Please try again in a moment." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="support-chat">
      <div ref={scrollRef} className={`flex-1 space-y-4 overflow-y-auto ${compact ? "p-4" : "p-5"}`} data-testid="support-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`support-msg-${m.role}`}>
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center ${m.role === "user" ? "bg-brand-dark text-white" : "bg-klein/10 text-klein"}`}>
              {m.role === "user" ? <User size={14} /> : <Bot size={14} />}
            </div>
            <div className={`max-w-[80%] whitespace-pre-wrap break-words px-3 py-2 text-sm leading-relaxed ${
              m.role === "user" ? "bg-brand-dark text-white" : "border border-black/10 bg-white text-slate-700"
            }`}>
              {m.content}
              {m.escalated && (
                <span className="mt-1.5 block font-mono-data text-[10px] uppercase tracking-wide text-warning" data-testid="support-escalated-badge">
                  · escalated to our team
                </span>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="support-typing">
            <Loader2 size={14} className="animate-spin" /> Assistant is typing…
          </div>
        )}
      </div>
      <div className="border-t border-black/10 p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="support-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder="Ask about pricing, billing, scans…"
            className="max-h-28 min-h-[42px] flex-1 resize-none border border-black/15 bg-white px-3 py-2 text-sm focus:border-klein focus:outline-none"
          />
          <button
            data-testid="support-send"
            onClick={send}
            disabled={sending || !input.trim()}
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center bg-klein text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
            aria-label="Send message"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
          <LifeBuoy size={11} /> Answers use our product docs. Complex issues are escalated to a human.
        </p>
      </div>
    </div>
  );
}

export default SupportChat;
